(ns app.domain.group.config.model
  (:require [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]))


(def default-pre-consultation :group.config.pre-consultation/not-applicable)


(def pre-consultation-choice
  #{:group.config.pre-consultation/required
    :group.config.pre-consultation/optional
    :group.config.pre-consultation/not-applicable})

(def pre-consultation-expiration-steps
  [:group-config.pre-consultation-expiration-enforce-step/submit
   :group-config.pre-consultation-expiration-enforce-step/deem-complete])

(def pre-consultation-expiration-default-step
  :group-config.pre-consultation-expiration-enforce-step/submit)


(def default-archive-on-finish :group.config.archive-on-finish/optional)


(def archive-on-finish-choice
  #{:group.config.archive-on-finish/required
    :group.config.archive-on-finish/optional
    :group.config.archive-on-finish/not-applicable})


(def allow-workspace-creation-values
  "\"Allow workspace creation\" tells to whom it should be possible to create
  a workspace with the given category/work type/work target. Here the values
  are ordered by their restrictiveness so that the least restrictive is first."
  [:group.config.allow-workspace-creation/everyone
   :group.config.allow-workspace-creation/authority
   :group.config.allow-workspace-creation/none])


(def default-allow-workspace-creation-value (first allow-workspace-creation-values))


(defn allow-workspace-creation-choice
  ([]
   allow-workspace-creation-values)
  ([parent-value]
   (drop-while #(not= % parent-value) allow-workspace-creation-values)))


(defn get-allow-workspace-creation-weight
  "Get weight of given allow workspace creation value. Greater weight means more restrictive visibility."
  [value]
  (p/find-index allow-workspace-creation-values #{value}))


(defn effective-allow-workspace-creation-value
  "Returns the effective (or most restrictive) allow workspace creation value"
  [& values]
  (let [values (->> values
                    (keep (set allow-workspace-creation-values))
                    (cons default-allow-workspace-creation-value))]
    (apply max-key get-allow-workspace-creation-weight values)))


(defn allow-workspace-creation-value?
  "Checks if the given (already resolved) allow workspace creation value permits creation for the user."
  [authority-user? value]
  (case value
    :group.config.allow-workspace-creation/everyone true
    :group.config.allow-workspace-creation/authority authority-user?
    false))


(defn inherited-allow-workspace-creation-value
  "Returns the effective allow workspace creation value of the config, taking all its ancestors into account."
  [config]
  (->> config
       (iterate :entity/parent)
       (take-while some?)
       (map :group.config/allow-workspace-creation)
       (apply effective-allow-workspace-creation-value)))


(defn allow-workspace-creation?
  "Checks if it is allowed to create a workspace or complaint with the given configuration.
  Takes parent configurations into account if wanted."
  ([authority-user? config]
   (allow-workspace-creation? authority-user? false config))
  ([authority-user? check-parents? config]
   (let [effective-value (if check-parents?
                           (inherited-allow-workspace-creation-value config)
                           (effective-allow-workspace-creation-value (:group.config/allow-workspace-creation config)))]
     (allow-workspace-creation-value? authority-user? effective-value))))


(defn all-allow-workspace-creation?
  "Returns true if workspace, or complaint, creation is allowed for all items in the configuration tuple, false
   otherwise. For nil items creation is allowed. Does not check if items have real parent-child relationship."
  [authority-user? [root category work-type-or-types work-target]]
  (let [work-types (if (set? work-type-or-types)
                     work-type-or-types
                     [work-type-or-types])]
    (every? (partial allow-workspace-creation? authority-user?)
            (concat [root category] work-types [work-target]))))


(def permit-types-without-description #{:permit-type/CE :permit-type/BS :permit-type/LS :permit-type/PI :permit-type/PWO})

(def multi-select-levels
  "Targets where multiple configurations may apply to a single workspace"
  #{:group-config.level/work-type
    :group-config.level/work-target})

(defmethod du/is-active? :entity.type/group-config
  [{state :domain/state}]
  (= state :group-config.state/enabled))

(def ^{:arglists '([group-config])} child-configs
  (child/child-entities-of-type-fn :entity.type/group-config))

(defn active-child-configs [group-config]
  (->> group-config child-configs (filter du/is-active?)))

(defn active-allowed-child-configs [authority-user? group-config]
  (->> group-config
       child-configs
       (filter (every-pred du/is-active?
                           (partial allow-workspace-creation? authority-user?)))))

(defn soft-removed? [group-config]
  (case (:domain/state group-config)
    (:group-config.state/removed :group-config.state/hidden) true
    false))

(defn default-work-set? [{:group.config/keys [default-work-type-gc default-work-target-gc]}]
  (and (some? default-work-type-gc)     ; Both are required; see WIN-1408
       (some? default-work-target-gc)))


(defn in-project-type-fn [project-type]
  (fn [group-config]
    (-> group-config :group.config/project-types (contains? project-type))))


(defmethod i18n-model/get-localized-string-attrs :entity.type/review-config [_]
  [:review.config/name])

(defmethod i18n-model/get-localized-string-attrs :entity.type/group-config [_]
  [:group.config/complaint-sign-off-disclaimers
   :group.config/names])

(defmethod group-model/get-authority-group :entity.type/review-config [review-config]
  (:domain/authority-group review-config))

(defmethod group-model/get-authority-group :entity.type/group-config [group-config]
  (:group.config/group group-config))


(def ^{:arglists '([config lang])} localized-name
  (i18n-model/translation-fallback-fn :group.config/names))

(def ^{:arglists '([config lang])} localized-disclaimer
  (i18n-model/translation-fallback-fn :group.config/complaint-sign-off-disclaimers))


(defn sort-group-configs [language group-configs]
  (->> group-configs
       (sort-by #(localized-name % language))))

(defn keep-as-set
  "Given a config-tuple, return a union of value extracted with `f`"
  [f coll]
  (->> coll
       (mapcat (fn [set-or-config]
                 (if (set? set-or-config)
                   (map f set-or-config)
                   [(f set-or-config)])))
       (apply concat)
       set))

(defn resolve-hidden-property-attributes [workspace]
  (->> (workspace-model/default-config-path workspace)
       (keep-as-set :group.config/hidden-property-attribute-templates)))

(defn workspace-primary-property-attributes
  [{:keys [:workspace/primary-property :domain/authority-group] :as workspace}]
  (let [attribute-available-for-ag? (comp (fn [{:keys [:property-attribute-template/visible-in-groups]}]
                                            (contains? (set visible-in-groups) authority-group))
                                          :property-attribute/template)
        hidden-templates            (resolve-hidden-property-attributes workspace)
        attribute-not-hidden?       (comp not
                                          (partial contains? hidden-templates)
                                          :property-attribute/template)]
    (->> primary-property
         :property-attribute/_property
         (filter (every-pred attribute-available-for-ag?
                             attribute-not-hidden?)))))

(defn resolve-application-inspections [workspace]
  (->> (workspace-model/default-config-path workspace)
       (keep-as-set :group.config/application-inspection-templates)))

(defn resolve-applicable-roles-for-target
  "Returns a set of roles required for the given group and target-seq."
  [group-configs]
  (keep-as-set :group.config/applicable-party-roles group-configs))

(defmethod du/is-active? :entity.type/role-config
  [{:role.config/keys [form-schema]}]
  (du/is-active? form-schema))

(defn category-name [workspace lang]
  (some-> workspace
          :workspace/category-group-config
          (localized-name lang)))

(defn additional-category-names [workspace lang]
  (some->> workspace
           :workspace/additional-categories
           seq
           (map #(localized-name % lang))
           sort
           (str/join ", ")))

(defn work-type-name [workspace lang]
  (some->> workspace
           :workspace/work-type-group-configs
           seq
           (map #(localized-name % lang))
           sort
           (str/join ", ")))

(defn work-target-name [workspace lang]
  (some-> workspace
          :workspace/work-target-group-config
          (localized-name lang)))

(defn resolve-boolean
  "A `true` boolean value anywhere in the config tree should propagate down"
  [attr workspace]
  (->> (workspace-model/default-config-path workspace)
       (some attr)
       boolean))


(defn resolve-rollover
  "Return the rollover value for workspace.
  Start looking from the leaf upwards and if none found default to auth-group's value.
  If that is also nil, default to true."
  [workspace]
  (let [config-rollover?     (some->> (workspace-model/default-config-path workspace)
                                      reverse
                                      (keep :group.config/municipal-case-id-rollover)
                                      first)
        auth-group-rollover? (some->> workspace
                                      :domain/authority-group
                                      :group/municipal-case-id-rollover)]
    (cond
      (some? config-rollover?)     config-rollover?
      (some? auth-group-rollover?) auth-group-rollover?
      :else                        true)))


(defn resolve-value
  "Resolve a group-config value for a workspace. Note that for booleans, the config UI shows
  that a `true` propagates down, overriding any falses below. This function does not do so,
  therefore with booleans use `resolve-boolean` instead."
  [attr workspace]
  (->> (workspace-model/default-config-path workspace)
       reverse
       (keep attr)
       first))


(defn resolve-config-value
  "Given a single group-config value, traverse to parents until a given value is found.

  Also works for booleans, similar to `resolve-boolean`, in that a `true` anywhere
  propagates down (although it may return `nil` even for an explicit `false`)."
  [attr group-config]
  (if (set? group-config)
    (every? (partial resolve-config-value attr) group-config)
    (loop [node group-config]
      (if-let [value (get node attr)]
        value
        (when-let [parent (:entity/parent node)]
          (recur parent))))))

(defn- config-path-tiers-agree-on-express-issuance?
  "True unless some tier of the config path vetoes express issuance. Only the multi-work-type tier
  (a set of peer configs) can veto: a selected work type that does not have express issuance enabled
  vetoes the work types that do. The single-config tiers keep the usual inheritance semantics, where
  a value configured higher up applies to the tiers below it."
  [config-path]
  (every? (fn [tier]
            (or (not (set? tier))
                (every? :group.config/express-issuance? tier)))
          config-path))

(defn resolve-express-issuance?
  "Resolve whether express issuance is enabled for the workspace.

  Enabled when express issuance is configured anywhere on the config path and no selected work type
  vetoes it. See `config-path-tiers-agree-on-express-issuance?`."
  [workspace]
  (let [config-path (workspace-model/default-config-path workspace)]
    (and (boolean (some :group.config/express-issuance?
                        (workspace-model/flatten-config-path config-path)))
         (config-path-tiers-agree-on-express-issuance? config-path))))

(defn resolve-express-issuance-template
  "Resolve the verdict template to use for express issuance, from the most specific tier that
  configures one.

  Returns `nil` when the selected work types disagree about the template, because there is no basis
  for picking one of them. Callers must fall back to manual issuance when there is no template."
  [workspace]
  (let [config-path (workspace-model/default-config-path workspace)
        templates   (fn [tier]
                      (if (set? tier)
                        (into #{} (keep :group.config/express-issuance-template) tier)
                        (some-> (:group.config/express-issuance-template tier) hash-set)))]
    (when-not (->> config-path
                   (map templates)
                   (some #(< 1 (count %))))
      (->> config-path
           reverse
           (some (comp first templates))))))


(defn find-active-gc-case-id-config
  "Return the last group config on the `config-path` that has a non-black municipal-case-id-prefix configured."
  [config-path]
  (-> config-path
      reverse
      (p/find-first #(some-> % :group.config/municipal-case-id-prefix str-util/blank->nil))))

(defn find-active-gc-attr
  "Return the val from the last group config on the `config-path` that has a non-blank attr"
  [config-path attr]
  (->> config-path
       reverse
       (some #(-> % attr str-util/blank->nil))))

(defn resolve-municipal-case-id-value
  "Municipal case id configuration attributes are special in that they booleans are not inherited
  and a config is ignored if it does not have a custom prefix value (custom case id config requires it)"
  [group-config-attr group-attr workspace]
  (if-some [group-config-value (-> (workspace-model/default-config-path workspace)
                                   (find-active-gc-attr group-config-attr))]
    group-config-value
    (get (:domain/authority-group workspace) group-attr)))

(defn resolve-municipal-case-id-source-for-configs
  [configs]
  (->> configs
       reverse
       (some #(or (:group.config/municipal-case-id-source %)
                  (:group/municipal-case-id-source %)))))

(defn resolve-municipal-case-id-source-for-ws
  "Find municipal case id source from group config or fallback to group case id source"
  ([workspace authority-group]
   (or (-> workspace
           workspace-model/default-config-path
           resolve-municipal-case-id-source-for-configs)
       (-> authority-group
           :group/municipal-case-id-source)))
  ([workspace]
   (resolve-municipal-case-id-source-for-ws workspace
                                            (:domain/authority-group workspace))))

(defn new-case-id-required?
  "Returns true if the configuration sequence in `new-config-path` has a different `municipal-case-id-prefix`
  than what the workspace currently has, i.e. if the new configuration will require a case id change."
  [{group :domain/authority-group :as workspace} new-config-path]
  (if (and (:workspace/municipal-case-id workspace)
           (-> workspace
               resolve-municipal-case-id-source-for-ws
               (= :case-id-source/custom-id)))
    (let [new-prefix (-> new-config-path
                         workspace-model/flatten-config-path
                         find-active-gc-case-id-config
                         :group.config/municipal-case-id-prefix
                         (or (:group/municipal-case-id-prefix group)))
          old-prefix (resolve-municipal-case-id-value :group.config/municipal-case-id-prefix
                                                      :group/municipal-case-id-prefix
                                                      workspace)]
      (not= new-prefix old-prefix))
    false))


(def verdict-default-identity
  "Function that returns a unique identifier for a verdict-default entity under one
  group-config"
  (juxt :verdict-default-config/section-id
        :verdict-default-config/applies-to))

(defn resolve-verdict-defaults [workspace]
  (->> (workspace-model/default-config-path workspace)
       workspace-model/flatten-config-path
       (mapcat :group.config/verdict-defaults)
       (reduce (fn [defaults default]
                 (assoc defaults (verdict-default-identity default) default))
               {})
       vals))

(defn resolve-form-schemas
  "Get default schemas configured for the given group-configs, for a specific schema type."
  [configs schema-type]
  (->> (keep-as-set :group.config/form-schemas configs)
       (filter du/is-active?)
       (filter #(= (:form.schema/type %) schema-type))
       set))

(defn resolve-sign-off-declarations
  "Returns applicable sign-off declarations for configs."
  [configs]
  (keep-as-set :group.config/sign-off-declarations configs))

(defn resolve-require-sign-off-after-changes-requested?
  [feature-flags workspace]
  (and (contains? feature-flags :feature-flag.id/reviews-in-submitted)
       (resolve-boolean :group.config/require-sign-off-after-changes-requested? workspace)))

(defn default-approver-groups
  [group-configs]
  (keep-as-set :group.config/default-approver-groups group-configs))

(defn resolve-omit-execution [workspace]
  (let [conf-val (resolve-value :group.config/omit-execution-phase? workspace)]
    (if (some? conf-val)
      conf-val
      (contains? ptc-model/application-only-types (:workspace/permit-type workspace)))))

(def resolve-include-conditions-in-verdict
  "Check if the Condition section(s) should be included in the verdict for a particular workspace"
  (partial resolve-boolean :group.config/conditions-in-verdict-template?))

(defn resolve-role-forms-for-target
  "Returns a map of (role -> form-schema-entity) of party role based forms
   for the given sequence of group configs."
  [group-configs]
  (->> group-configs
       (keep-as-set :group.config/role-configs)
       (filter du/is-active?)
       (reduce (fn [acc {:role.config/keys [form-schema party-role]}]
                 (assoc acc party-role form-schema))
               {})))

(defn resolve-pre-con-enforce-step [workspace]
  (or (resolve-value :group.config/pre-consultation-expiration-enforce-step workspace)
      pre-consultation-expiration-default-step))

(defn config-path-as-string [lang configs]
  (->> configs
       (remove #(-> % :group.config/level (= :group-config.level/permit-type))) ;; skip permit type from output
       (map (fn [config]
              (if (set? config)
                (->> config
                     (map #(localized-name % lang))
                     sort
                     (str/join " | "))
                (localized-name config lang))))
       (str/join " - ")))

(defn leaf-configs-to-path-string
  "Output the workspace configuration path from category down based on the provided `leaf-configs` which can be
   a single leaf-config or a set of them, especially if the `permit-type` supports multiple leaf
   configs in one workspace."
  [lang permit-type leaf-configs]
  (->> leaf-configs
       util/->set
       (workspace-model/leaf->config-path permit-type)
       (config-path-as-string lang)))

(def resolve-permit-sign-off
  (partial resolve-boolean :group.config/permit-sign-off?))

(defn localized-names [configs lang]
  (->> configs
       (map #(localized-name % lang))
       sort
       (str/join ", ")))

(defn building-permit-like? [config]
  (-> config :group.config/process-flow (= :process-flow/land-use)))

(def complaint-severity-use
  [:group-config.complaint-severity-use/required
   :group-config.complaint-severity-use/optional
   :group-config.complaint-severity-use/hidden])

(def default-complaint-severity-use
  :group-config.complaint-severity-use/hidden)

(def complaint-severity
  "Severity levels ordered from lowest to highest; sorting relies on this order"
  [:complaint-severity/low
   :complaint-severity/standard
   :complaint-severity/high
   :complaint-severity/emergency])

(def complaint-severity-config-state
  [:complaint-severity-config.state/enabled
   :complaint-severity-config.state/disabled])

(def default-visibility-state
  {:complaint-severity/low       :complaint-severity-config.state/enabled
   :complaint-severity/standard  :complaint-severity-config.state/enabled
   :complaint-severity/high      :complaint-severity-config.state/enabled
   :complaint-severity/emergency :complaint-severity-config.state/disabled})

(defmethod du/is-active? :entity.type/complaint-severity-config
  [complaint-severity-config]
  (= :complaint-severity-config.state/enabled
     (or (-> complaint-severity-config :domain/state)
         (-> complaint-severity-config :complaint-severity-config/severity default-visibility-state))))
